import { useEffect, useMemo, useState, type HTMLAttributes } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { debounce } from "@mui/material/utils";
import { SxProps, Theme } from "@mui/material/styles";
import {
  ClioService,
  MatterPreviewResponse,
  NetDocsMatterResponse,
  NetDocsService,
} from "../../../api";

type CommonProps<T> = {
  value: T | null;
  onChange: (value: T | null) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  noOptionsText?: string;
  /** Outer Autocomplete sx */
  sx?: SxProps<Theme>;
  /** TextField sx */
  textFieldSx?: SxProps<Theme>;
  /** Minimum width of the open options dropdown (can exceed the input width). */
  dropdownMinWidth?: number | string;
};

type ClioMatterBaseProps = Omit<CommonProps<MatterPreviewResponse>, "value" | "onChange">;

type ClioMatterSingleProps = ClioMatterBaseProps & {
  multiple?: false;
  value: MatterPreviewResponse | null;
  onChange: (value: MatterPreviewResponse | null) => void;
};

type ClioMatterMultiProps = ClioMatterBaseProps & {
  multiple: true;
  value: MatterPreviewResponse[];
  onChange: (value: MatterPreviewResponse[]) => void;
};

function getDescriptionLabel(option: { description?: string | null } | string) {
  return typeof option === "string" ? option : (option.description ?? "");
}

/** Debounced Clio matter search Autocomplete (single or multi). */
export function ClioMatterAutocomplete(props: ClioMatterSingleProps | ClioMatterMultiProps) {
  const {
    label = "Matter",
    disabled = false,
    required = false,
    size = "medium",
    fullWidth = true,
    noOptionsText = "No matters",
    sx,
    textFieldSx,
    multiple = false,
    value,
    onChange,
  } = props;

  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<readonly MatterPreviewResponse[]>([]);

  const selectedList: MatterPreviewResponse[] = multiple
    ? ((value as MatterPreviewResponse[]) ?? [])
    : value
      ? [value as MatterPreviewResponse]
      : [];

  const selectedIdsKey = selectedList.map((m) => m.id).join(",");

  const fetchMatters = useMemo(
    () =>
      debounce((input: string) => {
        ClioService.getApiClioMatters(input).then((response) => {
          let next: MatterPreviewResponse[] = [...selectedList];
          if (response?.length) {
            const ids = new Set(next.map((m) => m.id));
            response.forEach((m) => {
              if (!ids.has(m.id)) next.push(m);
            });
          }
          setOptions(next);
        });
      }, 200),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIdsKey]
  );

  useEffect(() => {
    if (!inputValue.trim()) {
      setOptions(selectedList);
      return;
    }
    fetchMatters(inputValue);
  }, [inputValue, fetchMatters, selectedIdsKey]);

  const sharedProps = {
    disabled,
    fullWidth,
    size,
    getOptionLabel: (option: MatterPreviewResponse) => option.description ?? "",
    isOptionEqualToValue: (a: MatterPreviewResponse, b: MatterPreviewResponse) =>
      a.id === b.id,
    filterOptions: (x: MatterPreviewResponse[]) => x,
    options: [...options],
    autoComplete: true,
    includeInputInList: true,
    filterSelectedOptions: true,
    noOptionsText,
    onInputChange: (_event: unknown, newInputValue: string) =>
      setInputValue(newInputValue),
    sx,
    renderInput: (params: any) => (
      <TextField
        {...params}
        label={label}
        required={required}
        fullWidth={fullWidth}
        size={size}
        sx={textFieldSx}
      />
    ),
    renderOption: (
      optionProps: HTMLAttributes<HTMLLIElement>,
      option: MatterPreviewResponse
    ) => (
      <li {...optionProps} key={option.id ?? option.description}>
        <Typography variant="body2" color="text.secondary">
          {option.description}
        </Typography>
      </li>
    ),
  };

  if (multiple) {
    return (
      <Autocomplete
        {...sharedProps}
        multiple
        value={(value as MatterPreviewResponse[]) ?? []}
        onChange={(_event, selectedValue) =>
          (onChange as ClioMatterMultiProps["onChange"])(selectedValue)
        }
      />
    );
  }

  return (
    <Autocomplete
      {...sharedProps}
      value={(value as MatterPreviewResponse | null) ?? null}
      onChange={(_event, selectedValue) =>
        (onChange as ClioMatterSingleProps["onChange"])(selectedValue)
      }
    />
  );
}

/** Debounced NetDocuments matter search Autocomplete. */
export function NetDocsMatterAutocomplete({
  value,
  onChange,
  label = "NetDocuments matter",
  disabled = false,
  required = false,
  size = "small",
  fullWidth = true,
  noOptionsText = "No matters",
  sx,
  textFieldSx,
  dropdownMinWidth = 560,
}: CommonProps<NetDocsMatterResponse>) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<readonly NetDocsMatterResponse[]>([]);

  const fetchMatters = useMemo(
    () =>
      debounce((input: string) => {
        NetDocsService.getNetDocsMatters(input).then((response) => {
          let next: NetDocsMatterResponse[] = value ? [value] : [];
          if (response?.length) {
            const keys = new Set(next.map((m) => m.key));
            response.forEach((m) => {
              if (!keys.has(m.key)) next.push(m);
            });
          }
          setOptions(next);
        });
      }, 200),
    [value]
  );

  useEffect(() => {
    if (!inputValue.trim()) {
      setOptions(value ? [value] : []);
      return;
    }
    fetchMatters(inputValue);
  }, [inputValue, value, fetchMatters]);

  return (
    <Autocomplete
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      getOptionLabel={getDescriptionLabel}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      noOptionsText={noOptionsText}
      onChange={(_event, selectedValue) => onChange(selectedValue)}
      onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
      sx={sx}
      componentsProps={{
        paper: {
          sx: {
            minWidth: dropdownMinWidth,
            width: dropdownMinWidth,
            maxWidth: "min(90vw, 720px)",
          },
        },
        popper: {
          placement: "bottom-start",
          style: { width: dropdownMinWidth, minWidth: dropdownMinWidth },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          fullWidth={fullWidth}
          size={size}
          sx={textFieldSx}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.key}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap", pr: 1 }}
          >
            {option.description}
          </Typography>
        </li>
      )}
    />
  );
}
